import { useEffect, useMemo, useRef, useState } from 'react';
import { copyShareLink } from '../lib/copyShareLink';
import { freshConversationParams } from '../lib/conversationParams';
import { answerActions, beginAssistantRetry, restoredAnswerFailure } from '../lib/answerActions';
import { Link, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  askApi,
  createSessionShareApi,
  getPublicSessionShareApi,
  getSessionApi,
  getSessionsApi,
  listSessionSharesApi,
  revokeSessionShareApi,
  speciesSearchApi,
  trackEvent,
  type SessionItem,
  type SessionShareItem,
  type SessionTurn,
  type BookCard,
  type SpeciesRef,
} from '../api';
import { toast } from '../toast';
import { downloadShareCard } from '../shareCard';
import { citationHref, citationEntryName } from '../lib/citationLinks';
import ExportPreview, { type ExportArtifact } from '../ExportPreview';
import { useClientAuth } from '../auth';
import { isSubmitEnter } from '../keyboard';
import { imageLink } from '../lib/imageUrl';
import { rehypeScientificNames, scientificNamesFrom } from '../lib/scientificNames';
import ScientificText from '../ScientificText';
import './Chat.css';

// 思考中动效：发送后到首字之间展示。label 随真实进度切换——
// meta 事件到达前=「分析意图」，到达(intent 已就绪)后=「检索图书 & 组织答案」。
function ThinkingIndicator({ hasIntent }: { hasIntent: boolean }) {
  return (
    <div className="thinking">
      <span className="thinking-dots"><i></i><i></i><i></i></span>
      <span className="thinking-label">
        {hasIntent ? '检索图书、组织答案中…' : '理解问题、分析意图中…'}
      </span>
    </div>
  );
}

// 问答正文 Markdown 渲染：支持表格(GFM)、加粗、列表；rehype-raw 让后端注入的
// <sup>N</sup> 引用上标正常显示。内容来自本系统 LLM（受控），故允许原始 HTML。
function MarkdownAnswer({ text, streaming, scientificNames = [] }: { text: string; streaming?: boolean; scientificNames?: string[] }) {
  const md = (text || '').replace(/\[\[c(\d+)\]\]/g, '<sup>$1</sup>'); // 兜底：残留占位符转上标
  return (
    <div className="md-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, [rehypeScientificNames, { names: scientificNames }]]}>{md}</ReactMarkdown>
      {streaming && <span className="md-caret">▍</span>}
    </div>
  );
}

interface Citation {
  n: number;
  chunk_id?: string;
  source_url?: string;
  book_id?: string;
  book_title?: string;
  chapter_title?: string;
  page?: number;
  text?: string;
  score?: number;
}

interface AssistantImage {
  url?: string;
  caption?: string;
  emoji?: string;
}

function AssistantImageCard({ image }: { image: AssistantImage }) {
  const [failed, setFailed] = useState(false);
  const caption = image.caption?.trim() || '相关水产图片';

  useEffect(() => setFailed(false), [image.url]);

  return (
    <figure className="pic">
      <div className="pic-media">
        {image.url && !failed ? (
          <img
            src={image.url}
            alt={caption}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="image-fallback" role="img" aria-label={`${caption}（图片暂不可用）`}>
            <span aria-hidden="true">{image.emoji ?? '🖼️'}</span>
            <small>图片暂不可用</small>
          </div>
        )}
      </div>
      <figcaption className="cap">
        <span className="pic-caption">{caption}</span>
        {!failed && imageLink(image.url) && <a className="pic-original" href={imageLink(image.url)} target="_blank" rel="noopener noreferrer" aria-label={`查看原图：${caption}`}>查看原图 <span aria-hidden="true">↗</span></a>}
      </figcaption>
    </figure>
  );
}

interface IntentMeta {
  intent?: string;
  entities?: string[];
  filters?: Record<string, unknown>;
  need_image?: boolean;
  confidence?: number;
}

interface Message {
  id: number;
  role: 'user' | 'ai';
  text: string;
  citations?: Citation[];
  images?: AssistantImage[];
  intent?: IntentMeta;
  followups?: string[];
  clarifications?: string[];
  books?: BookCard[];
  relatedSpecies?: SpeciesRef[];
  route?: string;
  streaming?: boolean;
  error?: { code?: string; message: string };
  retryText?: string;
}

// 会话标题→emoji；相对时间
function sessionEmoji(t?: string): string {
  if (!t) return '💬';
  if (/虾/.test(t)) return '🦞';
  if (/蟹/.test(t)) return '🦀';
  if (/鱼|鲶|江团|鳜|鲈/.test(t)) return '🐟';
  return '💬';
}
function relTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (days <= 0) return `今天 ${hh}:${mm}`;
  if (days === 1) return '昨天';
  if (days < 30) return `${days} 天前`;
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

const QUESTION_BUILDERS = [
  (name: string) => `${name}的分类地位是什么？`,
  (name: string) => `${name}主要分布在哪里？`,
  (name: string) => `${name}有哪些养殖模式？`,
] as const;

function speciesEmoji(name: string): string {
  if (/虾/.test(name)) return '🦐';
  if (/蟹/.test(name)) return '🦀';
  if (/贝|蚬|螺|蛎|蛤/.test(name)) return '🐚';
  if (/藻/.test(name)) return '🌿';
  if (/鳖|龟|蛙|鲵/.test(name)) return '🐢';
  return '🐟';
}

// 把引用标记统一渲染为上标。兼容两种来源：
//   - 模板格式 [[cN]]
//   - 模型有时直接输出的 <sup>N</sup> 原生 HTML（否则会被转义成可见的字面标签）
export default function ChatPage() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get('q') ?? '';
  const queryFilters=useMemo(()=>{try {const raw=JSON.parse(params.get('filters')||'{}');const keys=new Set(['category','region','production_area','waterway','feature','habit']);return Object.fromEntries(Object.entries(raw).filter(([key,value])=>keys.has(key) && typeof value==='string' && value.trim() && value!=='全部'));}catch{return {}; }},[params]);
  // `session` 仅兼容同一所有者的历史恢复；跨访客分享必须使用不可反推的 `share` 令牌。
  const sharedSessionId = params.get('session') ?? '';
  const publicShareToken = params.get('share') ?? '';

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [isPublicShare, setIsPublicShare] = useState(false);
  const [restoreError, setRestoreError] = useState('');
  const [sharePanelOpen, setSharePanelOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shareItems, setShareItems] = useState<SessionShareItem[]>([]);
  const [sharePending, setSharePending] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);
  const [exportArtifact, setExportArtifact] = useState<ExportArtifact | null>(null);
  const [createdShare, setCreatedShare] = useState<{ id: string; url: string } | null>(null);
  const [revokingShareId, setRevokingShareId] = useState<string | null>(null);
  const [suggestedSpecies, setSuggestedSpecies] = useState<SpeciesRef[]>([]);
  const [suggestionBatch, setSuggestionBatch] = useState(0);
  const msgIdRef = useRef(1);
  const sentInitialRef = useRef(false);
  const restoredSharedSessionRef = useRef(false);
  const restoredPublicShareRef = useRef(false);
  const msgsRef = useRef<HTMLDivElement>(null);
  const authToken = useClientAuth((state) => state.token);
  useEffect(() => { setCreatedShare(null); }, [sessionId, authToken]);

  // 仅问答页锁定整页滚动（固定双栏布局，内部各栏自滚）；离开时恢复，避免影响其它页。
  useEffect(() => {
    document.documentElement.classList.add('kp-chat-lock');
    document.body.classList.add('kp-chat-lock');
    return () => {
      document.documentElement.classList.remove('kp-chat-lock');
      document.body.classList.remove('kp-chat-lock');
    };
  }, []);

  // 真实会话列表（来自 qa.sessions）。挂载时加载，每轮答完后刷新。
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const loadSessions = () => {
    const requestedToken = useClientAuth.getState().token;
    if (!requestedToken) { setSessions([]); return; }
    getSessionsApi().then((items) => {
      if (useClientAuth.getState().token === requestedToken) setSessions(items);
    }).catch(() => {
      if (useClientAuth.getState().token === requestedToken) setSessions([]);
    });
  };
  useEffect(() => { loadSessions(); }, [authToken]);

  // 推荐题只从已收录的图鉴条目生成，避免固定示例超出当前资料范围。
  useEffect(() => {
    let cancelled = false;
    speciesSearchApi('', 12, 0, undefined, undefined, true)
      .then((data: SpeciesRef[] | { items?: SpeciesRef[] }) => {
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data.items ?? []);
        setSuggestedSpecies(items.filter((item) => item.name.trim()));
        setSuggestionBatch(0);
      })
      .catch(() => { if (!cancelled) setSuggestedSpecies([]); });
    return () => { cancelled = true; };
  }, []);

  const suggestedQuestions = useMemo(() => {
    const start = suggestionBatch * 3;
    return suggestedSpecies.slice(start, start + 3).map((species, index) => ({
      ico: speciesEmoji(species.name),
      text: QUESTION_BUILDERS[(start + index) % QUESTION_BUILDERS.length](species.name),
    }));
  }, [suggestedSpecies, suggestionBatch]);
  const suggestionBatchCount = Math.ceil(suggestedSpecies.length / 3);

  function turnMessages(turns: SessionTurn[]): Message[] {
    return turns
      .sort((a, b) => a.order_no - b.order_no)
      .flatMap((t): Message[] => {
        const message: Message = {
        id: msgIdRef.current++,
        role: t.role === 'user' ? 'user' : 'ai',
        text: t.content,
        intent: t.intent && typeof t.intent === 'object' ? t.intent as IntentMeta : undefined,
        citations: Array.isArray(t.citations)
          ? t.citations.map((c, index) => ({
              n: c?.index ?? c?.n ?? index + 1,
              chunk_id: c?.chunk_id,
              source_url: c?.source_url,
              book_id: c?.book_id,
              book_title: c?.book_title,
              chapter_title: c?.chapter_title,
              page: c?.page,
              text: c?.text,
              score: c?.score,
            }))
          : undefined,
        images: Array.isArray(t.images)
          ? t.images.map((image) => ({ url: image?.url, caption: image?.caption, emoji: image?.emoji }))
          : undefined,
        books: Array.isArray(t.enrichments?.books) ? t.enrichments.books : undefined,
        relatedSpecies: Array.isArray(t.enrichments?.related_species) ? t.enrichments.related_species : undefined,
        followups: Array.isArray(t.enrichments?.followups) ? t.enrichments.followups : undefined,
        clarifications: Array.isArray(t.enrichments?.clarifications) ? t.enrichments.clarifications : undefined,
        route: typeof t.route === 'string' ? t.route : undefined,
        streaming: false,
        };
        const failure = restoredAnswerFailure(t.role, t.content, t.enrichments?.generation_error);
        return failure ? [message, { ...failure, id: msgIdRef.current++ }] : [message];
      });
  }

  // 恢复私有历史会话：所有权由服务端的 JWT/guest Cookie 判定。
  async function restoreSession(id: string) {
    if (id === sessionId && !isPublicShare) return;
    setSessionId(id);
    setIsPublicShare(false);
    setRestoreError('');
    setMessages([]);
    try {
      const turns = await getSessionApi(id);
      setMessages(turnMessages(turns));
    } catch { setRestoreError('无法读取这段对话，请检查访问权限或稍后重试。'); }
  }

  async function restorePublicShare(token: string) {
    setSessionId(undefined);
    setIsPublicShare(true);
    setRestoreError('');
    setMessages([]);
    try {
      const share = await getPublicSessionShareApi(token);
      setMessages(turnMessages(share.turns));
      trackEvent('share_opened', { path: '/chat', metadata: { type: 'public_snapshot' } });
    } catch {
      setMessages([]);
      setIsPublicShare(false);
      setRestoreError('分享链接已失效、撤销或不存在。请联系分享者获取新链接。');
    }
  }

  function newSession() {
    setParams(freshConversationParams(params), { replace: true });
    setCreatedShare(null);
    setRestoreError('');
    setMessages([]);
    setSessionId(undefined);
    setIsPublicShare(false);
    setSharePanelOpen(false);
    setShareItems([]);
    setInput('');
  }

  // 导出当前对话为 txt
  function exportConversation() {
    if (!messages.length) { toast('当前没有可导出的对话'); return; }
    const text = messages
      .map((m) => `${m.role === 'user' ? '我' : '鱼虾小智'}：\n${m.text}`)
      .join('\n\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `科普趣答对话_${new Date().toISOString().slice(0, 10)}.txt`;
    setExportArtifact({ url: `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`, filename: a.download, text });
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    toast('已发起导出，请查看浏览器下载记录');
  }
  // 分享：服务端将当前私有会话冻结为可撤销的公开快照，URL 不携带会话 ID。
  async function shareLink() {
    if (creatingShare) return;
    if (!sessionId) {
      toast(isPublicShare ? '公开快照不能再次生成分享链接' : '请先完成一轮问答后再分享');
      return;
    }
    setCreatingShare(true);
    try {
      const share = await createSessionShareApi(sessionId);
      const url = `${location.origin}/chat?share=${encodeURIComponent(share.token)}`;
      setCreatedShare({ id: share.share_id, url });
      trackEvent('share_created', {
        session_id: sessionId,
        path: '/chat',
        metadata: { type: 'public_snapshot' },
      });
      const copied = await copyShareLink(url, navigator.clipboard);
      toast(copied ? '公开分享链接已复制' : '链接已创建，请从下方复制或打开');
    } catch {
      toast('创建分享链接失败，请稍后重试');
    } finally {
      setCreatingShare(false);
    }
  }

  async function toggleShareManager() {
    if (sharePanelOpen) {
      setSharePanelOpen(false);
      return;
    }
    if (!sessionId || isPublicShare) {
      toast('分享的对话没有可管理的链接');
      return;
    }
    setSharePending(true);
    try {
      setShareItems(await listSessionSharesApi(sessionId));
      setSharePanelOpen(true);
    } catch {
      toast('读取分享记录失败，请稍后重试');
    } finally {
      setSharePending(false);
    }
  }

  async function revokeShare(shareId: string) {
    if (!sessionId) return;
    setRevokingShareId(shareId);
    try {
      await revokeSessionShareApi(sessionId, shareId);
      setCreatedShare((share) => share?.id === shareId ? null : share);
      setShareItems((items) => items.map((item) => (
        item.id === shareId ? { ...item, revoked_at: new Date().toISOString() } : item
      )));
      toast('公开分享链接已撤销');
    } catch {
      toast('撤销失败，请稍后重试');
    } finally {
      setRevokingShareId(null);
    }
  }

  // 最新的 AI 消息，用于驱动右栏（意图 / 引用 / 置信度）和 chat-head 副标题
  const lastAi = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.role === 'ai') return messages[i]!;
    }
    return undefined;
  }, [messages]);
  const lastUser = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.role === 'user') return messages[i]!;
    }
    return undefined;
  }, [messages]);
  const isSending = messages.some((message) => message.role === 'ai' && message.streaming);

  async function downloadConversationCard() {
    if (!messages.length) {
      toast('当前没有可生成的问答分享卡');
      return;
    }
    const answer = lastAi?.text?.replace(/\[\[c\d+\]\]/g, '').trim();
    const question = lastUser?.text?.trim() || '水产知识问答';
    const source = lastAi?.citations?.find((citation) => citation.book_title)?.book_title;
    const filename = `问答分享卡_${new Date().toISOString().slice(0, 10)}.png`;
    const ok = await downloadShareCard({
      title: question,
      subtitle: '鱼虾小智 · AI 水产科普问答',
      body: answer || '问答正在生成，请稍后再生成分享卡。',
      source: source ? `资料来源：${source}` : '资料来源：科普趣答知识库',
      filename,
    }, (url) => setExportArtifact({ url, filename }));
    trackEvent('share_created', {
      session_id: sessionId,
      path: '/chat',
      metadata: { type: 'card' },
    });
    toast(ok ? '已发起分享卡下载，请查看浏览器下载记录' : '当前浏览器无法生成分享卡，请复制对话链接');
  }

  // 自动滚到底
  useEffect(() => {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  function sendMessage(text: string, retryAiId?: number) {
    const t = text.trim();
    if (!t || isSending) return;
    if (!authToken) {
      const next = freshConversationParams(params);
      next.set('q', t);
      setParams(next, { replace: true });
      return;
    }
    if (publicShareToken) setParams(freshConversationParams(params), { replace: true });
    setRestoreError('');
    setInput('');
    // 公开快照只读；从其中继续提问时切换为全新的私有会话，不能续写分享者会话。
    const startsPrivateSession = isPublicShare;
    if (startsPrivateSession) setIsPublicShare(false);
    const retry = retryAiId === undefined ? null : beginAssistantRetry(messages, retryAiId);
    if (retryAiId !== undefined && !retry) return;
    const userId = retry ? 0 : msgIdRef.current++;
    const aiId = retry?.aiId ?? msgIdRef.current++;
    if (retry) setMessages(retry.messages);
    else setMessages((prev) => (startsPrivateSession ? [
      { id: userId, role: 'user', text: t },
      { id: aiId, role: 'ai', text: '', streaming: true, citations: [], images: [] },
    ] : [
      ...prev,
      { id: userId, role: 'user', text: t },
      { id: aiId, role: 'ai', text: '', streaming: true, citations: [], images: [] },
    ]));

    // Render server deltas immediately. Replaying a complete, source-checked
    // answer character by character delays long tables and repeated Markdown
    // layout; the completion event supplies the authoritative final content.
    const receivedCitations: Citation[] = [];
    const receivedImages: AssistantImage[] = [];
    let completed = false;

    function completeMessage(d: any) {
      if (completed) return;
      completed = true;
      trackEvent('ask_completed', {
        session_id: d?.session_id ?? sessionId,
        path: '/chat',
        metadata: { citation_count: Array.isArray(d?.citations) ? d.citations.length : receivedCitations.length },
      });
      setMessages((prev) => prev.map((msg) => msg.id === aiId
        ? {
            ...msg,
            streaming: false,
            text: typeof d?.content === 'string' ? d.content : msg.text,
            citations: Array.isArray(d?.citations)
              ? d.citations.map((c: any, index: number) => ({
                  n: c?.index ?? c?.n ?? index + 1,
                  chunk_id: c?.chunk_id,
              source_url: c?.source_url,
              book_id: c?.book_id,
                  book_title: c?.book_title,
                  chapter_title: c?.chapter_title,
                  page: c?.page,
                  text: c?.text,
                  score: c?.score,
                }))
              : receivedCitations.length ? receivedCitations : msg.citations,
            images: Array.isArray(d?.images)
              ? d.images.map((image: any) => ({ url: image?.url, caption: image?.caption, emoji: image?.emoji }))
              : receivedImages.length ? receivedImages : msg.images,
            followups: Array.isArray(d?.followups) ? d.followups : msg.followups,
            clarifications: Array.isArray(d?.clarifications) ? d.clarifications : msg.clarifications,
            books: Array.isArray(d?.books) ? d.books : msg.books,
            relatedSpecies: Array.isArray(d?.related_species) ? d.related_species : msg.relatedSpecies,
            route: typeof d?.route === 'string' ? d.route : msg.route,
          }
        : msg));
      loadSessions(); // 新会话/新轮次后刷新左侧列表
    }

    function failMessage(message: string, code?: string) {
      completed = true;
      setMessages((prev) => prev.map((msg) => msg.id === aiId
        ? {
            ...msg,
            streaming: false,
            // 失败是一次请求状态，不是回答正文。清掉可能残留的半截 token，
            // 避免 parseAiText 把错误文案包装成“核心结论 1”。
            text: '',
            error: { code, message },
            retryText: t,
          }
        : msg));
    }

    askApi(t, sessionId, {
      onMeta: (m) => {
        if (m?.session_id && !sessionId) {
          setSessionId(m.session_id);
          // Consume the entry question once. Reloading must restore this
          // conversation rather than submit the search question a second time.
          restoredSharedSessionRef.current = true;
          const next = new URLSearchParams(params);
          next.delete('q');
          next.delete('share');
          next.set('session', m.session_id);
          setParams(next, { replace: true });
        }
        if (m?.intent) {
          setMessages((prev) => prev.map((msg) => msg.id === aiId ? { ...msg, intent: m.intent as IntentMeta } : msg));
        }
      },
      onToken: (delta) => {
        if (!delta || completed) return;
        setMessages((prev) => prev.map((msg) => msg.id === aiId ? { ...msg, text: msg.text + delta } : msg));
      },
      onCitation: (c) => {
        const n = c?.n ?? receivedCitations.length + 1;
        receivedCitations.push({ n, chunk_id: c?.chunk_id,
              source_url: c?.source_url,
              book_id: c?.book_id, book_title: c?.book_title, chapter_title: c?.chapter_title, page: c?.page, text: c?.text, score: c?.score });
      },
      onImage: (img) => {
        receivedImages.push({ url: img?.url, caption: img?.caption, emoji: img?.emoji });
      },
      onDone: (d) => {
        completeMessage(d);
      },
      onError: (error) => {
        failMessage(error?.message || '服务暂不可用，请稍后再试', error?.code);
      },
    }, Boolean(retry), queryFilters).catch((error: unknown) => {
      const message = error instanceof Error && error.message
        ? error.message
        : '服务暂不可用，请稍后再试';
      failMessage(message);
    });
  }

  // 公开令牌优先加载只读快照；旧的 `session` 参数只兼容同一所有者的历史恢复。
  useEffect(() => {
    if (!restoredPublicShareRef.current && publicShareToken) {
      restoredPublicShareRef.current = true;
      void restorePublicShare(publicShareToken);
    } else if (!restoredSharedSessionRef.current && sharedSessionId) {
      restoredSharedSessionRef.current = true;
      trackEvent('share_opened', { session_id: sharedSessionId, path: '/chat' });
      void restoreSession(sharedSessionId);
    } else if (!sentInitialRef.current && initialQ) {
      sentInitialRef.current = true;
      sendMessage(initialQ);
    }
  }, [initialQ, publicShareToken, sharedSessionId]);

  return (
    <div className="container chat-container">
      <div className="chat-layout">

        {/* 左：会话历史 */}
        <div className="card side">
          <div className="head">
            <h4>📜 我的会话</h4>
            <span className="chip outline sm" style={{ padding: '2px 9px', fontSize: '10.5px' }}>共 {sessions.length} 条</span>
          </div>
          <div className="new-chat" onClick={newSession}>＋ 发起新会话</div>

          {sessions.length === 0 && (
            <div className="m" style={{ padding: '8px 12px', color: 'var(--ink-3)' }}>暂无历史会话，问一句试试 →</div>
          )}
          {sessions.map((s) => (
            <div
              key={s.id}
              className={`entry ${s.id === sessionId ? 'active' : ''}`}
              onClick={() => restoreSession(s.id)}
            >
              <div className="ico">{sessionEmoji(s.title)}</div>
              <div>
                <div className="t">{s.title || '未命名会话'}</div>
                <div className="m">{s.turns} 条消息 · {relTime(s.updated_at)}</div>
              </div>
            </div>
          ))}

          {suggestedQuestions.length > 0 && (<>
            <div className="divider"></div>
            <div className="head topic-head">
              <h4>📚 图鉴可问</h4>
              {suggestionBatchCount > 1 && (
                <button
                  type="button"
                  className="topic-refresh"
                  onClick={() => setSuggestionBatch((current) => (current + 1) % suggestionBatchCount)}
                >换一批</button>
              )}
            </div>
            {suggestedQuestions.map((question) => (
              <div key={question.text} className="entry" onClick={() => sendMessage(question.text)}>
                <div className="ico">{question.ico}</div>
                <div>
                  <div className="t">{question.text}</div>
                  <div className="m">来自已收录图鉴</div>
                </div>
              </div>
            ))}
          </>)}
        </div>

        {/* 中：主对话 */}
        <div className="card chat-main">
          <div className="chat-head">
            <div className="left">
              <h3>鱼虾小智{isPublicShare ? ' · 分享对话' : ''}</h3>
            </div>
            <div className="flex gap-8 chat-actions-desktop">
              <button className="chip outline" type="button" disabled={creatingShare} onClick={() => void shareLink()}>{creatingShare ? '创建中…' : '🔗 分享'}</button>
              <button className="chip outline" type="button" onClick={() => void toggleShareManager()} disabled={sharePending}>
                {sharePending ? '读取中…' : '管理分享'}
              </button>
              <button className="chip outline" type="button" onClick={() => void downloadConversationCard()}>📥 分享卡</button>
              <button className="chip outline" type="button" onClick={exportConversation}>⬇︎ 导出</button>
              <button className="chip outline" type="button" onClick={newSession}>＋ 新会话</button>
            </div>
            <div className="chat-actions-mobile">
              <button
                className="mobile-head-action"
                type="button"
                aria-label="发起新会话"
                onClick={() => { setMobileMenuOpen(false); newSession(); }}
              >＋</button>
              <button
                className="mobile-head-action"
                type="button"
                aria-label="更多会话操作"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((open) => !open)}
              >•••</button>
              {mobileMenuOpen && (
                <div className="mobile-action-menu" role="menu" aria-label="会话操作">
                  <button type="button" role="menuitem" disabled={creatingShare} onClick={() => { setMobileMenuOpen(false); void shareLink(); }}>{creatingShare ? '创建中…' : '🔗 分享链接'}</button>
                  <button type="button" role="menuitem" onClick={() => { setMobileMenuOpen(false); void toggleShareManager(); }}>☰ 管理分享</button>
                  <button type="button" role="menuitem" onClick={() => { setMobileMenuOpen(false); void downloadConversationCard(); }}>▣ 生成分享卡</button>
                  <button type="button" role="menuitem" onClick={() => { setMobileMenuOpen(false); exportConversation(); }}>↓ 导出对话</button>
                </div>
              )}
            </div>
          </div>

          {createdShare && (
            <div className="share-manager" role="region" aria-label="新建公开分享链接">
              <label htmlFor="created-share-url">公开链接（持有链接的人可查看当前对话快照）</label>
              <input id="created-share-url" readOnly value={createdShare.url} onFocus={(event) => event.currentTarget.select()} />
              <a href={createdShare.url} target="_blank" rel="noopener noreferrer">打开分享快照 ↗</a>
              <button type="button" onClick={() => setCreatedShare(null)}>收起链接</button>
            </div>
          )}
          {sharePanelOpen && (
            <div className="share-manager" role="region" aria-label="公开分享管理">
              <div className="share-manager-title">
                <span>公开分享链接</span>
                <button className="share-manager-close" type="button" onClick={() => setSharePanelOpen(false)} aria-label="关闭分享管理">×</button>
              </div>
              {shareItems.length === 0 ? (
                <span className="share-manager-empty">尚未创建公开分享链接</span>
              ) : shareItems.map((share) => {
                const unavailable = !!share.revoked_at || new Date(share.expires_at).getTime() <= Date.now();
                return (
                  <div className="share-manager-row" key={share.id}>
                    <span>{unavailable ? (share.revoked_at ? '已撤销' : '已到期') : `有效至 ${new Date(share.expires_at).toLocaleDateString()}`}</span>
                    {!unavailable && (
                      <button
                        className="btn danger sm"
                        type="button"
                        disabled={revokingShareId === share.id}
                        onClick={() => void revokeShare(share.id)}
                      >
                        {revokingShareId === share.id ? '撤销中…' : '撤销'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="msgs" ref={msgsRef}>
            {restoreError && <div className="card" role="alert">{restoreError}<button className="btn ghost" type="button" onClick={newSession}>开始新对话</button></div>}
            {messages.length === 0 && !restoreError && (
              <EmptyHint questions={suggestedQuestions.map((item) => item.text)} onPick={(q) => sendMessage(q)} />
            )}
            {messages.map((m) => m.role === 'user' ? (
              <div key={m.id} className="msg user">{m.text}</div>
            ) : (
              <AiBubble key={m.id} msg={m} onFollowup={sendMessage}
                onRetry={() => sendMessage(m.retryText ?? '', m.id)}
                canRetry={!isPublicShare && messages.at(-1)?.id === m.id} sending={isSending} />
            ))}
          </div>

          <div className="composer-wrap">
            <div className="composer">
              <textarea
                rows={1}
                aria-label="输入问题"
                placeholder="输入问题…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (isSubmitEnter(e)) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
              />
              <button
                className="btn primary"
                aria-label="发送问题"
                disabled={!input.trim() || isSending}
                onClick={() => sendMessage(input)}
              ><span className="send-label">{isSending ? '回答中…' : '发送 ↗'}</span></button>
            </div>
          </div>
        </div>

      </div>
      <ExportPreview artifact={exportArtifact} onClose={() => setExportArtifact(null)} />
    </div>
  );
}

function AnswerBookCard({ book }: { book: BookCard }) {
  const [coverFailed, setCoverFailed] = useState(false);
  useEffect(() => {
    trackEvent('book_impression', { book_id: book.id, path: '/chat', source: 'answer_card' });
  }, [book.id]);

  return (
    <div className="book-card">
      <div className="cover">
        {book.cover_url && !coverFailed
          ? <img src={book.cover_url} alt="" onError={() => setCoverFailed(true)} />
          : '📘'}
      </div>
      <div className="info">
        <h5>{book.title}</h5>
        <div className="sub">{book.authors.length ? book.authors.join(' / ') : '权威水产图书'}</div>
        <div className="actions">
          <Link
            to={`/book/${book.id}`}
            className="chip"
            style={{ textDecoration: 'none' }}
            onClick={() => trackEvent('book_click', { book_id: book.id, path: '/chat', source: 'answer_card' })}
          >图书详情</Link>
          {book.purchase_url && (
            <a
              href={book.purchase_url}
              className="chip active"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
              onClick={() => trackEvent('book_jump', { book_id: book.id, path: '/chat', source: 'answer_card' })}
            >🛒 前往购买</a>
          )}
        </div>
      </div>
    </div>
  );
}

function AiBubble({ msg, onFollowup, onRetry, canRetry, sending }: {
  msg: Message;
  onFollowup: (question: string) => void;
  onRetry: () => void;
  canRetry: boolean;
  sending: boolean;
}) {
  const isRefusal = msg.route?.startsWith('refuse_') ?? false;
  const actions = answerActions(msg.route, msg.followups, msg.clarifications);
  const modeLabel = msg.streaming ? '正在分析' : msg.route === 'direct_answer'
    ? '直接回答'
    : msg.route === 'pi_agent_image_grounded'
      ? '据图解读'
    : msg.route === 'clarification_required'
      ? '需要确认对象'
    : msg.route === 'knowledge_fallback'
      ? 'AI 通用回答'
    : msg.route === 'safety_refusal'
      ? '温馨提示'
      : isRefusal
        ? '资料不足'
      : msg.error
        ? '暂时未回答'
      : '资料回答';
  const cits = msg.citations ?? [];
  const sourceGroups = useMemo(() => {
    const groups = new Map<string,{key:string;title:string;citations:Array<{citation:Citation;index:number}>}>();
    cits.forEach((citation,index) => {
      const key=citation.book_id || citation.book_title || 'unknown';
      if(!groups.has(key)) groups.set(key,{key,title:citation.book_title || '参考资料',citations:[]});
      groups.get(key)!.citations.push({citation,index});
    });
    return [...groups.values()];
  },[cits]);
  const scientificNames = scientificNamesFrom(msg.text, ...cits.map((citation) => citation.text ?? ''));
  const uniqBooks = useMemo(() => {
    const seen = new Set<string>();
    const out: BookCard[] = [];
    for (const c of cits) {
      if (!c.book_id || !c.book_title) continue;
      const key = c.book_id ?? c.book_title;
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ id: c.book_id, title: c.book_title, authors: [] });
      }
    }
    return out;
  }, [cits]);
  // An explicit empty array means the backend intentionally suppressed book
  // cards for this turn.  Only derive legacy cards from citations when the
  // `books` field is absent altogether.
  const books = msg.books !== undefined ? msg.books : uniqBooks;

  return (
    <div className="msg ai">
      {(isRefusal || msg.route === 'knowledge_fallback' || msg.route === 'clarification_required') && <span className="ai-tag">{modeLabel}</span>}

      {msg.streaming && !(msg.text && msg.text.length) ? (
        <ThinkingIndicator hasIntent={!!msg.intent} />
      ) : msg.error ? (
        <div className="answer-error" role="alert">
          <div className="answer-error-icon" aria-hidden="true">↻</div>
          <div className="answer-error-copy">
            <strong>这次没有生成成功</strong>
            <span>{msg.error.message}</span>
          </div>
          {msg.retryText && canRetry && (
            <button className="answer-retry" type="button" disabled={sending}
              onClick={onRetry}>重新回答</button>
          )}
        </div>
      ) : msg.text ? (
        <MarkdownAnswer text={msg.text} streaming={msg.streaming} scientificNames={scientificNames} />
      ) : null}



      {msg.images && msg.images.length > 0 && (
        <details className="answer-images"><summary>相关配图 <span>{msg.images.length} 张</span></summary><div className="image-grid">
          {msg.images.map((img, i) => <AssistantImageCard key={`${img.url ?? 'fallback'}-${i}`} image={img} />)}
        </div></details>
      )}

      {cits.length > 0 ? <details className="answer-sources">
        <summary><span>参考资料</span><span className="answer-sources-count">{cits.length} 处引用 · {sourceGroups.length} 本资料</span><span className="answer-sources-toggle">展开核对</span></summary>
        <div className="answer-sources-content">
          {sourceGroups.map(group => <section className="citation-book-group" key={group.key}>
            <h4>{group.title}</h4>
            <ol className="citation-list">
              {group.citations.map(({citation,index}) => {
                const href=citationHref(citation);
                const name=citationEntryName(citation.text) || citation.chapter_title || '引用资料';
                return <li className="citation-record" key={citation.chunk_id || `${citation.book_id}:${citation.page}:${index}`}>
                  <div className="citation-record-heading">
                    <span className="citation-number">{citation.n || index+1}</span>
                    {href ? <Link className="citation-original-link" to={href} target="_blank" rel="noopener noreferrer">{name}<span className="citation-page">PDF 第 {citation.page} 页 ↗</span></Link>
                      : <span>{name} · 暂无对应原文页</span>}
                  </div>
                  {citation.text && <details className="citation-excerpt"><summary>资料片段</summary><div className="citation-record-text"><ScientificText text={citation.text} /></div></details>}
                </li>;
              })}
            </ol>
          </section>)}
          {books.map(book => <AnswerBookCard key={book.id} book={book} />)}
      {!msg.streaming && msg.relatedSpecies && msg.relatedSpecies.length > 0 && (
        <div className="related-species">
          <h4>品种资料</h4>
          <div className="followup">
            {msg.relatedSpecies.map((species) => (
              <Link key={species.id} to={`/species/${species.id}`} className="chip" style={{ textDecoration: 'none' }}>
                {species.name}{species.category ? ` · ${species.category}` : ''}
              </Link>
            ))}
          </div>
        </div>
      )}

        </div>
      </details> : books.map(book => <AnswerBookCard key={book.id} book={book} />)}


      {!msg.streaming && actions.clarifications.length > 0 && (
        <div className="answer-clarifications">
          <h4>请补充信息</h4>
          {actions.clarifications.map((text) => <p key={text}>{text}</p>)}
          <p className="m">请在下方输入框补充后发送。</p>
        </div>
      )}
      {!msg.streaming && actions.questions.length > 0 && (
        <div className="followup">
          {actions.questions.map((q) => (
            <button key={q} type="button" className="chip followup-question"
              disabled={sending} onClick={() => onFollowup(q)}>{q}</button>
          ))}
        </div>
      )}

    </div>
  );
}

function EmptyHint({ questions, onPick }: { questions: string[]; onPick: (q: string) => void }) {
  return (
    <div className="msg ai empty-hint">
      <h4>想了解哪种水产？</h4>
      <p>查品种、问区别，答案附原书出处。</p>
      {questions.length > 0 && <div className="followup">
        {questions.map((q) => (
          <span key={q} className="chip" onClick={() => onPick(q)}>{q}</span>
        ))}
      </div>}
    </div>
  );
}
